import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  ButtonGroup,
  Button,
  Avatar,
  LinearProgress,
  Link,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

// Sample data for the chart
const chartData = [
  { day: 1, calls: 1000, minutes: 500 },
  { day: 5, calls: 1500, minutes: 800 },
  { day: 10, calls: 2000, minutes: 1200 },
  { day: 15, calls: 3000, minutes: 2000 },
  { day: 20, calls: 3800, minutes: 2800 },
  { day: 25, calls: 3500, minutes: 2600 },
  { day: 30, calls: 4000, minutes: 3000 },
  { day: 35, calls: 3800, minutes: 2800 },
  { day: 40, calls: 3000, minutes: 2200 },
];

// Sample data for companies
const companiesData = [
  {
    name: "Acme Corporation",
    initials: "AC",
    minutes: 28492,
    revenue: 14246,
    color: "#4285F4",
    bgColor: "#E8F0FE",
  },
  {
    name: "Widget Industries",
    initials: "WI",
    minutes: 24182,
    revenue: 12091,
    color: "#A142F4",
    bgColor: "#F3E8FE",
  },
  {
    name: "Global Services",
    initials: "GS",
    minutes: 19873,
    revenue: 9936,
    color: "#0F9D58",
    bgColor: "#E6F4EA",
  },
  {
    name: "TechTron Inc.",
    initials: "TT",
    minutes: 15634,
    revenue: 7817,
    color: "#EA4335",
    bgColor: "#FCE8E6",
  },
  {
    name: "StarCall Solutions",
    initials: "SC",
    minutes: 12428,
    revenue: 6214,
    color: "#FBBC04",
    bgColor: "#FEF7E0",
  },
];

// Find the maximum minutes to calculate progress percentages
const maxMinutes = Math.max(...companiesData.map((company) => company.minutes));

const PlatformActivity = () => {
  return (
    <Container maxWidth="xl" sx={{ mt: 5, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Platform Activity Chart */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              border: "1px solid #e5e7eb",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Platform Activity
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Call volumes and minutes analyzed
                </Typography>
              </Box>
              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{ borderRadius: 4 }}
              >
                <Button
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": { bgcolor: "#3367D6" },
                    borderRadius: "16px 0 0 16px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8em",
                    }}
                  >
                    Daily
                  </Typography>
                </Button>
                <Button sx={{ color: "text.secondary", borderRadius: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8em",
                    }}
                  >
                    Weekly
                  </Typography>
                </Button>
                <Button
                  sx={{
                    color: "text.secondary",
                    borderRadius: "0 16px 16px 0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8em",
                    }}
                  >
                    Monthly
                  </Typography>
                </Button>
              </ButtonGroup>
            </Box>

            <Box sx={{ height: 300, mt: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9e9e9e" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9e9e9e" }}
                    tickFormatter={(value) =>
                      value >= 1000 ? `${value / 1000}K` : value
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="calls"
                    stroke="#4285F4"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="minutes"
                    stroke="#A142F4"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 2 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#4285F4",
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Calls
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#A142F4",
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Minutes
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Top Companies */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e5e7eb",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="body1" fontWeight="medium">
                Top Companies
              </Typography>
              <Link
                variant="body2"
                href="#"
                underline="none"
                sx={{
                  color: "#4285F4",
                  fontWeight: "medium",
                }}
              >
                View All
              </Link>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                maxHeight: 370,
                overflow: "auto",
              }}
            >
              {companiesData.map((company, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: company.bgColor,
                      color: company.color,
                      fontWeight: "bold",
                      width: 38,
                      height: 38,
                    }}
                  >
                    <Typography> {company.initials}</Typography>
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {company.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {company.minutes.toLocaleString()} min
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(company.minutes / maxMinutes) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "#f0f0f0",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: company.color,
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="#4285F4"
                    fontWeight="medium"
                  >
                    ${company.revenue.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PlatformActivity;
